import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { createCustomEqual } from 'fast-equals';
import { isLatLngLiteral } from '@googlemaps/typescript-guards';
import { useMap } from '@components/map/map-context';
import { MAP_STYLES } from './map-styles';
const deepCompareEqualsForMaps = createCustomEqual(
// @ts-expect-error — fast-equals v5 changed createCustomEqual to use options object instead of callback
deepEqual => (a: any, b: any) => {
  if (isLatLngLiteral(a) || a instanceof google.maps.LatLng || isLatLngLiteral(b) || b instanceof google.maps.LatLng) {
    return new google.maps.LatLng(a).equals(new google.maps.LatLng(b));
  }
  return deepEqual(a, b);
});
const MapInstanceContext = React.createContext<google.maps.Map | undefined>(undefined);
export const useMapInstance = () => {
  return React.useContext(MapInstanceContext);
};
interface MapProps extends google.maps.MapOptions {
  children?: React.ReactNode;
  onClick?: (e: google.maps.MapMouseEvent) => void;
  onDragEnd?: (map: google.maps.Map) => void;
  onIdle?: (map: google.maps.Map) => void;
  onResize?: (map: google.maps.Map) => void;
  onZoomChanged?: (map: google.maps.Map) => void;
  style?: React.CSSProperties;
}
export const Map: React.FC<MapProps> = t0 => {
  const $ = _c(34);
  let children;
  let onClick;
  let onDragEnd;
  let onIdle;
  let onResize;
  let onZoomChanged;
  let props;
  let style;
  if ($[0] !== t0) {
    ({
      children,
      onClick,
      onDragEnd,
      onIdle,
      onResize,
      onZoomChanged,
      style,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = onClick;
    $[3] = onDragEnd;
    $[4] = onIdle;
    $[5] = onResize;
    $[6] = onZoomChanged;
    $[7] = props;
    $[8] = style;
  } else {
    children = $[1];
    onClick = $[2];
    onDragEnd = $[3];
    onIdle = $[4];
    onResize = $[5];
    onZoomChanged = $[6];
    props = $[7];
    style = $[8];
  }
  const {
    map,
    setMap
  } = useMap();
  const ref = React.useRef(null);
  let t1;
  if ($[9] !== map || $[10] !== setMap) {
    t1 = () => {
      if (ref.current && !map) {
        setMap(new window.google.maps.Map(ref.current, {
          styles: MAP_STYLES
        }));
      }
    };
    $[9] = map;
    $[10] = setMap;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  let t2;
  if ($[12] !== map) {
    t2 = [ref, map];
    $[12] = map;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  React.useEffect(t1, t2);
  let t3;
  let t4;
  if ($[14] !== map || $[15] !== props) {
    t3 = () => {
      if (map) {
        map.setOptions(props);
        google.maps.event.trigger(map, "resize");
      }
    };
    t4 = [map, props];
    $[14] = map;
    $[15] = props;
    $[16] = t3;
    $[17] = t4;
  } else {
    t3 = $[16];
    t4 = $[17];
  }
  useDeepCompareEffectForMaps(t3, t4);
  let t5;
  let t6;
  if ($[18] !== map || $[19] !== onClick || $[20] !== onDragEnd || $[21] !== onIdle || $[22] !== onResize || $[23] !== onZoomChanged) {
    t5 = () => {
      if (!map) {
        return;
      }
      const listeners = [];
      if (onClick) {
        listeners.push(map.addListener("click", onClick));
      }
      if (onIdle) {
        listeners.push(map.addListener("idle", () => onIdle(map)));
      }
      if (onZoomChanged) {
        listeners.push(map.addListener("zoom_changed", () => onZoomChanged(map)));
      }
      if (onResize) {
        listeners.push(map.addListener("resize", () => onResize(map)));
      }
      if (onDragEnd) {
        listeners.push(map.addListener("dragend", () => onDragEnd(map)));
      }
      return () => listeners.forEach(_temp);
    };
    t6 = [map, onClick, onIdle, onZoomChanged, onResize, onDragEnd];
    $[18] = map;
    $[19] = onClick;
    $[20] = onDragEnd;
    $[21] = onIdle;
    $[22] = onResize;
    $[23] = onZoomChanged;
    $[24] = t5;
    $[25] = t6;
  } else {
    t5 = $[24];
    t6 = $[25];
  }
  React.useEffect(t5, t6);
  let t7;
  if ($[26] !== style) {
    t7 = <div ref={ref} style={style} />;
    $[26] = style;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== children || $[29] !== map) {
    t8 = <MapInstanceContext.Provider value={map}>{children}</MapInstanceContext.Provider>;
    $[28] = children;
    $[29] = map;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== t7 || $[32] !== t8) {
    t9 = <>{t7}{t8}</>;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  return t9;
};
function useDeepCompareMemoize(value: unknown) {
  const ref = React.useRef<unknown>(null);

  // eslint-disable-next-line react-hooks/refs -- This is intentionally accessing refs during render for memoization (deep comparison pattern)
  if (!deepCompareEqualsForMaps(value, ref.current)) {
    // eslint-disable-next-line react-hooks/refs -- This is intentionally updating refs during render for memoization
    ref.current = value;
  }

  // eslint-disable-next-line react-hooks/refs -- This is intentionally accessing refs during render for memoization
  return ref.current;
}
function useDeepCompareEffectForMaps(callback: React.EffectCallback, dependencies: unknown[]) {
  React.useEffect(callback, dependencies.map(useDeepCompareMemoize));
}
interface MapMarkerProps extends google.maps.MarkerOptions {
  onDrag?: (e: google.maps.Marker) => void;
  onDragEnd?: (e: google.maps.Marker) => void;
  onDragStart?: (e: google.maps.Marker) => void;
  onClick?: (e: google.maps.Marker) => void;
  priceLabel?: string;
  hover?: boolean;
  index?: number;
  'data-testid'?: string;
}
export const getMapMarkerIcon = (labelText: string, hover: boolean) => {
  const textLength = labelText.length;
  const width = textLength * 7 + 8;
  const height = 28;
  const hoverScale = 1.1;
  const scale = hover ? hoverScale : 1;
  if (textLength === 0) {
    const fillColor = hover ? '#1E424C' : '#FFFFFF';
    return {
      path: google.maps.SymbolPath.CIRCLE,
      fillColor: fillColor,
      fillOpacity: 1,
      strokeColor: '#C0C0C0',
      strokeWeight: 1,
      scale: 12 * scale
    };
  }
  const fillColor = hover ? '#1E424C' : '#FFFFFF';
  return {
    path: `M 0,0 h ${width} a ${height / 2},${height / 2} 0 1,1 0,${height} h -${width} a ${height / 2},${height / 2} 0 1,1 0,-${height} z`,
    fillColor: fillColor,
    fillOpacity: 1,
    strokeColor: '#C0C0C0',
    strokeWeight: 1,
    scale: scale,
    anchor: new google.maps.Point(width / 2, height / 2),
    labelOrigin: new google.maps.Point(width / 2, height / 2 + 1)
  };
};
export const getMapMarkerLabel = (labelText: string, hover: boolean) => {
  const textLength = labelText.length;
  if (textLength === 0) {
    return undefined;
  }
  return {
    text: labelText,
    color: hover ? '#FFFFFF' : '#000000',
    fontSize: hover ? '16px' : '14px',
    fontWeight: 'bold'
  };
};
export const MapMarker: React.FC<MapMarkerProps> = t0 => {
  const $ = _c(34);
  let hover;
  let index;
  let onClick;
  let onDrag;
  let onDragEnd;
  let onDragStart;
  let priceLabel;
  let props;
  if ($[0] !== t0) {
    const {
      onDrag: t1,
      onDragEnd: t2,
      onDragStart: t3,
      onClick: t4,
      priceLabel: t5,
      hover: t6,
      index: t7,
      "data-testid": _testId,
      ...t8
    } = t0;
    onDrag = t1;
    onDragEnd = t2;
    onDragStart = t3;
    onClick = t4;
    priceLabel = t5;
    hover = t6;
    index = t7;
    props = t8;
    $[0] = t0;
    $[1] = hover;
    $[2] = index;
    $[3] = onClick;
    $[4] = onDrag;
    $[5] = onDragEnd;
    $[6] = onDragStart;
    $[7] = priceLabel;
    $[8] = props;
  } else {
    hover = $[1];
    index = $[2];
    onClick = $[3];
    onDrag = $[4];
    onDragEnd = $[5];
    onDragStart = $[6];
    priceLabel = $[7];
    props = $[8];
  }
  const mapInstance = useMapInstance();
  const [marker, setMarker] = React.useState();
  const zIndex = props.zIndex ?? (index ? index : 1);
  let t1;
  let t2;
  if ($[9] !== marker) {
    t1 = () => {
      if (!marker) {
        const newMarker = new google.maps.Marker({});
        setMarker(newMarker);
      }
      return () => {
        if (marker) {
          marker.setMap(null);
        }
      };
    };
    t2 = [marker];
    $[9] = marker;
    $[10] = t1;
    $[11] = t2;
  } else {
    t1 = $[10];
    t2 = $[11];
  }
  React.useEffect(t1, t2);
  let t3;
  let t4;
  if ($[12] !== mapInstance || $[13] !== marker) {
    t3 = () => {
      if (marker && mapInstance) {
        marker.setMap(mapInstance);
      }
    };
    t4 = [marker, mapInstance];
    $[12] = mapInstance;
    $[13] = marker;
    $[14] = t3;
    $[15] = t4;
  } else {
    t3 = $[14];
    t4 = $[15];
  }
  React.useEffect(t3, t4);
  let t5;
  if ($[16] !== hover || $[17] !== marker || $[18] !== onClick || $[19] !== onDrag || $[20] !== onDragEnd || $[21] !== onDragStart || $[22] !== priceLabel || $[23] !== props || $[24] !== zIndex) {
    t5 = () => {
      if (!marker) {
        return;
      }
      google.maps.event.clearInstanceListeners(marker);
      const resolvedIcon = priceLabel ? getMapMarkerIcon(priceLabel, false) : props.icon || getMapMarkerIcon("", false);
      const resolvedLabel = priceLabel ? getMapMarkerLabel(priceLabel, false) : undefined;
      marker.setOptions({
        ...props,
        icon: resolvedIcon,
        label: resolvedLabel,
        zIndex
      });
      if (priceLabel) {
        marker.addListener("mouseover", () => {
          marker.setZIndex(google.maps.Marker.MAX_ZINDEX + 1);
          marker.setIcon(getMapMarkerIcon(priceLabel, true));
          marker.setLabel(getMapMarkerLabel(priceLabel, true));
        });
        marker.addListener("mouseout", () => {
          marker.setZIndex(zIndex);
          marker.setIcon(getMapMarkerIcon(priceLabel, false));
          marker.setLabel(getMapMarkerLabel(priceLabel, false));
        });
      }
      if (onDrag) {
        marker.addListener("drag", () => onDrag(marker));
      }
      if (onDragEnd) {
        marker.addListener("dragend", () => onDragEnd(marker));
      }
      if (onDragStart) {
        marker.addListener("dragstart", () => onDragStart(marker));
      }
      if (onClick) {
        marker.addListener("click", () => onClick(marker));
      }
      if (hover) {
        marker.setZIndex(google.maps.Marker.MAX_ZINDEX + 1);
        if (priceLabel) {
          marker.setIcon(getMapMarkerIcon(priceLabel, true));
          marker.setLabel(getMapMarkerLabel(priceLabel, true));
        }
      }
      return () => {
        google.maps.event.clearInstanceListeners(marker);
      };
    };
    $[16] = hover;
    $[17] = marker;
    $[18] = onClick;
    $[19] = onDrag;
    $[20] = onDragEnd;
    $[21] = onDragStart;
    $[22] = priceLabel;
    $[23] = props;
    $[24] = zIndex;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  let t6;
  if ($[26] !== hover || $[27] !== marker || $[28] !== onClick || $[29] !== onDrag || $[30] !== onDragEnd || $[31] !== onDragStart || $[32] !== props) {
    t6 = [marker, props, onDrag, onDragEnd, onDragStart, onClick, hover];
    $[26] = hover;
    $[27] = marker;
    $[28] = onClick;
    $[29] = onDrag;
    $[30] = onDragEnd;
    $[31] = onDragStart;
    $[32] = props;
    $[33] = t6;
  } else {
    t6 = $[33];
  }
  useDeepCompareEffectForMaps(t5, t6);
  return null;
};
interface MapCircleProps extends google.maps.CircleOptions {
  onClick?: (e: google.maps.Circle) => void;
}
export const MapCircle: React.FC<MapCircleProps> = t0 => {
  const $ = _c(15);
  let onClick;
  let props;
  if ($[0] !== t0) {
    ({
      onClick,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = onClick;
    $[2] = props;
  } else {
    onClick = $[1];
    props = $[2];
  }
  const mapInstance = useMapInstance();
  const [circle, setCircle] = React.useState();
  let t1;
  let t2;
  if ($[3] !== circle) {
    t1 = () => {
      if (!circle) {
        setCircle(new google.maps.Circle());
      }
      return () => {
        if (circle) {
          circle.setMap(null);
        }
      };
    };
    t2 = [circle];
    $[3] = circle;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  React.useEffect(t1, t2);
  let t3;
  let t4;
  if ($[6] !== circle || $[7] !== mapInstance) {
    t3 = () => {
      if (circle && mapInstance) {
        circle.setMap(mapInstance);
      }
    };
    t4 = [circle, mapInstance];
    $[6] = circle;
    $[7] = mapInstance;
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  React.useEffect(t3, t4);
  let t5;
  let t6;
  if ($[10] !== circle || $[11] !== onClick || $[12] !== props) {
    t5 = () => {
      if (!circle) {
        return;
      }
      google.maps.event.clearInstanceListeners(circle);
      circle.setOptions(props);
      if (onClick) {
        circle.addListener("click", () => onClick(circle));
      }
      return () => {
        google.maps.event.clearInstanceListeners(circle);
      };
    };
    t6 = [circle, props, onClick];
    $[10] = circle;
    $[11] = onClick;
    $[12] = props;
    $[13] = t5;
    $[14] = t6;
  } else {
    t5 = $[13];
    t6 = $[14];
  }
  useDeepCompareEffectForMaps(t5, t6);
  return null;
};
export const MapMarkerDefaultIcon = {
  path: 0.0,
  scale: 11,
  fillColor: '#1E424C',
  fillOpacity: 1,
  strokeColor: '#FFFFFF',
  strokeWeight: 2
};
function _temp(listener) {
  return listener.remove();
}